'use client';

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { cn } from '@/lib/utils';

import type { PolymorphicComponentProps } from './types';

type StackProps<E extends React.ElementType = 'div'> =
    PolymorphicComponentProps<E> & {
        showEdgesFade?: boolean;
        fadeHeight?: number;
        fadeFromColor?: string;
    };

/**
 * Stack - Vertical flex container
 * Maps to Figma's auto layout with vertical direction
 *
 * @example
 * <Stack className="gap-4 items-start p-2">
 *   {children}
 * </Stack>
 *
 * @example
 * <Stack showEdgesFade fadeHeight={20}>
 *   {children}
 * </Stack>
 */
// Simple Stack component without fade functionality
const SimpleStackComponent = (
    {
        as,
        className,
        children,
        ...props
    }: Omit<StackProps, 'showEdgesFade' | 'fadeHeight' | 'fadeFromColor'>,
    ref: React.ForwardedRef<HTMLElement>,
) => {
    const Component = (as || 'div') as React.ElementType;
    return (
        <Component
            ref={ref}
            className={cn('flex flex-col', className)}
            {...props}
        >
            {children}
        </Component>
    );
};

const SimpleStack = forwardRef(SimpleStackComponent);

// Stack component with fade functionality
const StackWithFadeComponent = (
    {
        as,
        className,
        children,
        fadeHeight = 20,
        fadeFromColor = 'from-background',
        ...props
    }: Omit<StackProps, 'showEdgesFade'>,
    ref: React.ForwardedRef<HTMLElement>,
) => {
    const internalRef = useRef<HTMLElement>(null);
    const [showTopFadeState, setShowTopFadeState] = useState(false);
    const [showBottomFadeState, setShowBottomFadeState] = useState(false);

    // Merge refs: use internal ref for scroll detection, forward external ref
    useImperativeHandle(ref, () => internalRef.current as HTMLElement);

    const checkScrollPosition = useCallback((container: HTMLElement) => {
        if (!container) return;

        const scrollableElement = container.querySelector(
            '[data-scrollable]',
        ) as HTMLElement | null;

        if (!scrollableElement) {
            // If no scrollable element found, check the container itself
            const element = container;
            const { scrollTop, scrollHeight, clientHeight } = element;

            setShowTopFadeState(scrollTop > 0);
            setShowBottomFadeState(scrollTop + clientHeight < scrollHeight - 1);
        } else {
            const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

            setShowTopFadeState(scrollTop > 0);
            setShowBottomFadeState(scrollTop + clientHeight < scrollHeight - 1);
        }
    }, []);

    useLayoutEffect(() => {
        const container = internalRef.current;
        if (!container) return;

        // Frame 1 — baseline
        requestAnimationFrame(() => {
            checkScrollPosition(container);

            // Frame 2 — after full paint
            requestAnimationFrame(() => {
                checkScrollPosition(container);
            });
        });
    }, [checkScrollPosition]);

    useEffect(() => {
        const container = internalRef.current;
        if (!container) return;

        // Find scrollable element (could be the container or a child)
        const scrollableElement =
            container.querySelector('[data-scrollable]') || container;

        const checkScrollPositionCallback = () =>
            checkScrollPosition(container);

        scrollableElement.addEventListener(
            'scroll',
            checkScrollPositionCallback,
        );
        window.addEventListener('resize', checkScrollPositionCallback);

        return () => {
            scrollableElement.removeEventListener(
                'scroll',
                checkScrollPositionCallback,
            );
            window.removeEventListener('resize', checkScrollPositionCallback);
        };
    }, [checkScrollPosition]);

    return (
        <SimpleStack
            as={as}
            ref={internalRef}
            className={cn('relative', className)}
            {...props}
        >
            {children}
            <div
                className={cn(
                    fadeFromColor,
                    'pointer-events-none absolute top-0 right-0 left-0 bg-gradient-to-b to-transparent',
                    'overflow-hidden transition-all duration-100 ease-in-out',
                )}
                style={{
                    height: showTopFadeState ? `${fadeHeight}px` : '0px',
                    opacity: showTopFadeState ? 1 : 0,
                }}
                aria-hidden="true"
            />
            <div
                className={cn(
                    fadeFromColor,
                    'pointer-events-none absolute right-0 bottom-0 left-0 bg-gradient-to-t to-transparent',
                    'overflow-hidden transition-all duration-100 ease-in-out',
                )}
                style={{
                    height: showBottomFadeState ? `${fadeHeight}px` : '0px',
                    opacity: showBottomFadeState ? 1 : 0,
                }}
                aria-hidden="true"
            />
        </SimpleStack>
    );
};

const StackWithFade = forwardRef(StackWithFadeComponent);

// Main Stack component that conditionally renders simple or fade version
const StackComponent = (
    { showEdgesFade = false, ...props }: StackProps,
    ref: React.ForwardedRef<HTMLElement>,
) => {
    if (showEdgesFade) {
        return <StackWithFade ref={ref} {...props} />;
    }
    return <SimpleStack ref={ref} {...props} />;
};

export const Stack = forwardRef(StackComponent) as <
    E extends React.ElementType = 'div',
>(
    props: StackProps<E> & { ref?: React.Ref<HTMLElement> },
) => React.ReactElement;
